import { isUndefined } from 'lodash-es';
import { type KeyboardEvent } from 'react';

import { HistogramSelectionAreaTooltip } from './HistogramSelectionAreaTooltip.js';
import { HistogramSelectionDatapoints } from './HistogramSelectionDatapoints.js';
import { reduceExploreSelect } from '../../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { SelectionArea } from '../../../../core/components/selection-area/SelectionArea.js';
import type { RelativeAbsolutePosition } from '../../../../core/types/pointer-events.js';
import { useSelectState } from '../../../hooks/store/selectors.js';
import { useSetState } from '../../../hooks/store/state-setters.js';
import { useHistogramChartOverlayRef } from '../../../hooks/useHistogramChartOverlayRef.js';
import { useHistogramInteractions } from '../../../hooks/useHistogramInteractions.js';
import { useHistogramSelectionAreaRef } from '../../../hooks/useHistogramSelectionAreaRef.js';
import { useHistogramXFormatter } from '../../../hooks/useHistogramXFormatter.js';
import { useHistogramXScale } from '../../../hooks/useHistogramXScale.js';
import { useXAxisConfig } from '../../../hooks/useXAxisConfig.js';
import { useZoomToSelection } from '../../../hooks/useZoomToSelection.js';

export const HistogramSelectionArea = () => {
  const bottomFormatter = useHistogramXFormatter();
  const selectionAreaRef = useHistogramSelectionAreaRef();
  const { unit: xUnit } = useXAxisConfig();
  const { xScale } = useHistogramXScale();
  const chartRef = useHistogramChartOverlayRef();
  const chartBounds = chartRef.current?.getBoundingClientRect();
  const setState = useSetState();
  const zoomToSelection = useZoomToSelection();
  const selectState = useSelectState();
  const { zoomX } = useHistogramInteractions();

  if (isUndefined(selectState) || isUndefined(chartBounds)) {
    return null;
  }

  const { start, end, finished, accessible, variant } = selectState;
  const handleWrapperKeyDown = (event: KeyboardEvent) => {
    const canZoom = zoomX?.enabled;
    if (event.key === 'Enter' && canZoom) {
      zoomToSelection();
      chartRef.current?.focus();
    }
  };

  const handleSelectionChange = ({
    selection,
    accessible,
  }: {
    selection: [RelativeAbsolutePosition, RelativeAbsolutePosition];
    accessible: boolean;
  }) => {
    setState(
      reduceExploreSelect({
        start: selection[0],
        end: selection[1],
        finished: true,
        accessible,
        variant,
      }),
    );
  };

  return (
    <>
      {variant === 'select' ? (
        <>
          <HistogramSelectionDatapoints />
          <HistogramSelectionAreaTooltip />
        </>
      ) : null}
      <SelectionArea
        selection={[start, end]}
        bounds={chartBounds}
        variant={variant}
        focusable={accessible}
        interactable={finished}
        bottomFormatter={(value) =>
          bottomFormatter(xScale.invert(value), xUnit)
        }
        onKeyDown={handleWrapperKeyDown}
        onSelectionChange={handleSelectionChange}
        ref={selectionAreaRef}
      />
    </>
  );
};
